import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { getApiErrorMessage } from "@api/apiError";
import { useLockDate } from "@api/lock.api";
import {
    ScheduleBillingBulkFailureError,
    useMarkSchedulesBilledBulkMutation,
} from "@api/scheduleBilling.api";
import { validateFormikWithZodSchema } from "@helpers/form";
import { getFirstUnlockedDateOnly } from "@helpers/lock";
import {
    ScheduleBillingModalFooter,
    ScheduleBillingModalShell,
} from "./ScheduleBillingModalShell";
import {
    buildMarkBilledBulkFailureToast,
    buildMarkBilledBulkSuccessToast,
} from "./scheduleBillingCopy";
import { getDefaultBilledDate } from "./scheduleBillingRowHelpers";

export interface ScheduleBulkBillingTarget {
    applicationIds: string[];
    entityId: number;
}

const bulkBilledSchema = z.object({
    billedDate: z.string().date("Pick a billed date"),
});

type BulkBilledValues = z.infer<typeof bulkBilledSchema>;

const fields = createFormikFields<BulkBilledValues>();

const ScheduleMarkBilledBulkForm = ({
    target,
    onHide,
    onCompleted,
}: {
    target: ScheduleBulkBillingTarget;
    onHide: () => void;
    onCompleted: () => void;
}) => {
    const { toast, error } = useToaster();
    const lockDate = useLockDate(target.entityId);
    const firstUnlockedDate = getFirstUnlockedDateOnly(lockDate);
    const mutation = useMarkSchedulesBilledBulkMutation();

    const formik = useFormik<BulkBilledValues>({
        initialValues: {
            billedDate: getDefaultBilledDate(firstUnlockedDate),
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(bulkBilledSchema),
        onSubmit: async (values) => {
            try {
                const applications = await mutation.mutateAsync({
                    applicationIds: target.applicationIds,
                    entityId: target.entityId,
                    billedDate: values.billedDate,
                });
                toast(
                    buildMarkBilledBulkSuccessToast(applications.length),
                    undefined,
                    { variant: "success" },
                );
                onCompleted();
                onHide();
            } catch (mutationError) {
                error(
                    mutationError instanceof ScheduleBillingBulkFailureError
                        ? buildMarkBilledBulkFailureToast(
                              mutationError.failures,
                          )
                        : getApiErrorMessage(
                              mutationError,
                              "Failed to mark schedules as billed",
                          ),
                );
            }
        },
    });

    return (
        <FormikProvider value={formik}>
            <Form>
                <VStack gap={16}>
                    <fields.Date
                        name="billedDate"
                        label="Billed date"
                        minDate={firstUnlockedDate}
                    />
                    <ScheduleBillingModalFooter
                        isSubmitting={formik.isSubmitting}
                        onCancel={onHide}
                    />
                </VStack>
            </Form>
        </FormikProvider>
    );
};

interface ScheduleMarkBilledBulkModalProps {
    target: ScheduleBulkBillingTarget | null;
    onHide: () => void;
    onCompleted: () => void;
}

export const ScheduleMarkBilledBulkModal = ({
    target,
    onHide,
    onCompleted,
}: ScheduleMarkBilledBulkModalProps) => (
    <ScheduleBillingModalShell target={target} onHide={onHide}>
        {(displayedTarget) => (
            <ScheduleMarkBilledBulkForm
                target={displayedTarget}
                onHide={onHide}
                onCompleted={onCompleted}
            />
        )}
    </ScheduleBillingModalShell>
);
